import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { BrutalCard } from "./ui/BrutalCard";
import { BrutalButton } from "./ui/BrutalButton";
import { X, Smartphone } from "lucide-react";

const STORAGE_KEY = "pwa-banner-dismissed";
const COOKIE_CONSENT_KEY = "cookie-consent";
const SHOW_DELAY_MS = 3000;

export const PWAInstallBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Don't show on auth pages
    if (location.pathname.startsWith("/auth/")) {
      return;
    }

    // Don't show if already dismissed
    const isDismissed = localStorage.getItem(STORAGE_KEY);
    if (isDismissed) {
      return;
    }

    // Don't show if already installed (standalone mode)
    const isStandalone = 
      window.matchMedia("(display-mode: standalone)").matches || 
      (window.navigator as any).standalone === true;
    if (isStandalone) {
      return;
    }

    // Wait for cookie consent to be handled first
    const cookieConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!cookieConsent) {
      // Wait for cookie consent, then show PWA banner
      const checkCookieConsent = setInterval(() => {
        if (localStorage.getItem(COOKIE_CONSENT_KEY)) {
          clearInterval(checkCookieConsent);
          setTimeout(() => setShowBanner(true), SHOW_DELAY_MS);
        }
      }, 500);
      
      // Cleanup interval after 30 seconds max
      const timeout = setTimeout(() => clearInterval(checkCookieConsent), 30000);
      
      return () => {
        clearInterval(checkCookieConsent);
        clearTimeout(timeout);
      };
    }

    // Show after delay if cookie consent already given
    const timer = setTimeout(() => setShowBanner(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setShowBanner(false);
  };

  const goToInstall = () => {
    dismiss();
    navigate("/install");
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-black/50 backdrop-blur-sm">
      <div className="container mx-auto max-w-4xl">
        <BrutalCard className="relative">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 p-2 hover:bg-surface rounded"
            aria-label={t("common.close")}
          >
            <X size={20} />
          </button>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            
            <div className="flex-1 pr-8">
              <h2 className="font-heading text-lg mb-1">
                {t("pwaBanner.title")}
              </h2>
              <p className="text-sm text-foreground/70 mb-4">
                {t("pwaBanner.description")}
              </p>
              
              <div className="flex flex-wrap items-center gap-4">
                <button 
                  onClick={dismiss}
                  className="text-sm text-foreground/60 hover:text-foreground underline-offset-2 hover:underline transition-colors"
                >
                  {t("pwaBanner.dismiss")}
                </button>
                <BrutalButton size="sm" onClick={goToInstall}>
                  {t("pwaBanner.action")}
                </BrutalButton>
              </div>
            </div>
          </div>
        </BrutalCard>
      </div>
    </div>
  );
};
